import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Lock, ArrowRight, Sparkles } from 'lucide-react';
import StripeTrustBanner from './StripeTrustBanner';

const API = process.env.REACT_APP_BACKEND_URL;

const includes = [
  '0% commission on every order — forever',
  '45-day free trial — cancel anytime',
  'Direct Stripe payouts to your own account',
  'Premium digital menu + QR ordering',
  'Customer notifications via WhatsApp / SMS / Email',
  'Founding partner pricing locked in for 6 months',
];

const FoundingPricingSection = () => {
  const [starting, setStarting] = useState(false);

  const startTrial = async () => {
    setStarting(true);
    try {
      const token = localStorage.getItem('nofee_token');
      const origin = window.location.origin;

      if (!token) {
        window.location.href = `/register?next=billing`;
        return;
      }
      const res = await axios.post(
        `${API}/api/billing/create-checkout-session`,
        { origin_url: origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start trial. Please try again.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start trial.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <section
      id="pricing"
      className="relative bg-black py-16 lg:py-24 border-t border-white/[0.04] overflow-hidden"
      data-testid="founding-pricing-section"
    >
      <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_50%_30%,rgba(212,175,55,0.08),transparent_65%)]" />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center">
        {/* Left — pitch */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-4">
            Founding Restaurant Pricing
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-[1.05]">
            Keep more from every order.
            <br />
            <span className="text-gold-400">0% commission</span>, forever.
          </h2>
          <p className="mt-5 text-lg text-white/65 leading-relaxed max-w-xl">
            Flat monthly pricing. Payments go directly to your own Stripe account.
            NoFee never holds your money. No hidden fees.
            Built to help restaurants keep more of what they earn.
          </p>

          <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
            {includes.map((line) => (
              <li
                key={line}
                className="flex items-start gap-2 text-[14px] text-white/80 leading-snug"
              >
                <Check className="w-4 h-4 text-gold-400 mt-0.5 flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right — pricing card */}
        <div
          data-testid="founding-pricing-card"
          className="relative rounded-3xl bg-gradient-to-br from-zinc-950 to-black border border-gold-500/30 p-7 lg:p-9 shadow-[0_40px_90px_-30px_rgba(212,175,55,0.25)]"
        >
          <div className="absolute -top-3 left-7">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-300 to-gold-500 text-black text-[10px] font-black uppercase tracking-[0.16em] shadow-md">
              <Sparkles className="w-3 h-3" /> Founding Partner
            </span>
          </div>

          <div className="mt-2">
            <div className="text-[12px] uppercase tracking-[0.2em] text-white/55 font-semibold">
              Flat monthly pricing
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-6xl font-black text-white tracking-tight" data-testid="founding-price">
                $299
              </span>
              <span className="text-base text-white/55">/month</span>
            </div>
            <p className="mt-2 text-sm text-white/60 leading-snug">
              <span className="text-gold-300 font-semibold">First 6 months</span>{' '}
              · then <span className="text-white font-semibold">$349/month</span>.
              <br />
              <span className="text-white/45">Lock your founding pricing now.</span>
            </p>
          </div>

          <div className="my-6 h-px bg-gradient-to-r from-transparent via-zinc-700/80 to-transparent" />

          <div
            className="rounded-2xl bg-gold-500/[0.06] border border-gold-500/35 p-4 mb-5"
            data-testid="founding-trial-banner"
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-gold-400" />
              <span className="text-[13px] font-extrabold text-gold-300 tracking-tight">
                45-Day Free Trial
              </span>
            </div>
            <p className="text-[12px] text-white/65 leading-snug">
              Credit card required. No charge during trial. Cancel anytime.
            </p>
          </div>

          <button
            onClick={startTrial}
            disabled={starting}
            data-testid="founding-start-trial-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[15px] disabled:opacity-60 transition-all shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]"
          >
            {starting ? 'Starting…' : 'Start Free Trial'}
            {!starting && <ArrowRight className="w-4 h-4" />}
          </button>

          <div className="mt-3 flex items-center justify-center">
            <StripeTrustBanner variant="inline" dark />
          </div>

          <ul className="mt-5 grid grid-cols-2 gap-y-1.5 text-[11px] text-white/55">
            {[
              'Cancel anytime',
              'No setup fee',
              'Flat monthly pricing',
              'No commission fees',
            ].map((t) => (
              <li key={t} className="flex items-center gap-1.5">
                <Check className="w-3 h-3 text-emerald-400/80" /> {t}
              </li>
            ))}
          </ul>

          <div className="mt-5 pt-5 border-t border-white/[0.06] text-center">
            <a
              href="/savings-calculator"
              data-testid="homepage-savings-link"
              className="inline-flex items-center gap-1.5 text-[12px] text-white/45 hover:text-gold-300 transition-colors"
            >
              See how much you can save
              <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FoundingPricingSection;
